import type { ResultBucket, ResultContent, ScoringThresholds } from 'components/BuildVsBuyQuiz/config/types';
import type { ButtonConfigProps } from 'molecules/Button/config';

/**
 * Calculate dynamic scoring thresholds based on total question count
 *
 * AUTOMATIC SCALING:
 * - "Clear" result requires ~75-77% of answers in one direction
 * - "Leans" result requires ~60-62% of answers in one direction
 * - Everything else is "Toss Up"
 *
 * Examples:
 * - 5 questions:  clear=4 (80%), leans=3 (60%)
 * - 10 questions: clear=8 (80%), leans=6 (60%)
 * - 13 questions: clear=10 (77%), leans=8 (62%)
 * - 20 questions: clear=15 (75%), leans=12 (60%)
 *
 * @param totalQuestions - Total number of questions in the quiz
 * @returns ScoringThresholds with clear and leans thresholds
 */
export const calculateThresholds = (totalQuestions: number): ScoringThresholds => {
  // Clear threshold: ~75-80% of total questions
  const clearThreshold = Math.ceil(totalQuestions * 0.75);

  // Leans threshold: ~60% of total questions
  const leansThreshold = Math.ceil(totalQuestions * 0.6);

  return {
    clearThreshold,
    leansThreshold,
  };
};

/**
 * Calculate result bucket based on blue/orange answer counts
 * FULLY DYNAMIC - automatically adapts to any question count
 *
 * Scoring logic:
 * 1. Calculate thresholds based on total questions
 * 2. Check orange count first (BUY direction)
 * 3. Then check blue count (BUILD direction)
 * 4. Everything else is toss_up
 *
 * @param blueCount - Number of blue (BUILD) answers
 * @param orangeCount - Number of orange (BUY) answers
 * @param totalQuestions - Total number of questions (auto-calculated from blueCount + orangeCount)
 * @returns ResultBucket classification
 */
export const calculateResultBucket = (
  blueCount: number,
  orangeCount: number,
  totalQuestions?: number,
): ResultBucket => {
  // Auto-calculate total questions if not provided
  const total = totalQuestions || blueCount + orangeCount;

  // Get dynamic thresholds
  const { clearThreshold, leansThreshold } = calculateThresholds(total);

  // Check orange (BUY) direction first
  if (orangeCount >= clearThreshold) {
    return 'clear_buy';
  }
  if (orangeCount >= leansThreshold) {
    return 'leans_buy';
  }

  // Check blue (BUILD) direction
  if (blueCount >= clearThreshold) {
    return 'clear_build';
  }
  if (blueCount >= leansThreshold) {
    return 'leans_build';
  }

  // Everything else is balanced
  return 'toss_up';
};

/**
 * Default hardcoded result content for each bucket
 *
 * Used as fallback when Builder.io fields are not provided.
 * This allows the component to work out of the box while still
 * supporting full customization through Builder.
 */
export const DEFAULT_RESULT_CONTENT: Record<ResultBucket, ResultContent> = {
  clear_buy: {
    heading: 'You should buy an integration platform',
    description:
      'Requirements, timelines, or resources make buying the clear choice. Building might delay time-to-market and slow core product development.',
    buttons: [
      {
        label: 'Book a Demo',
        href: '/book-demo',
        hierarchy: 'primary',
      },
    ],
  },
  leans_buy: {
    heading: 'You should probably buy an integration platform',
    description:
      'You have some build capabilities, but most factors favor buying. Calculate the opportunity costs of diverting devs from your core product.',
    buttons: [
      {
        label: 'Book a Demo',
        href: '/book-demo',
        hierarchy: 'primary',
      },
    ],
  },
  toss_up: {
    heading: 'Consider both options carefully',
    description:
      'Your situation is balanced. Evaluate total cost of ownership, maintenance burden, and strategic priorities before deciding.',
    buttons: [
      {
        label: 'Talk to an Expert',
        href: '/book-demo',
        hierarchy: 'primary',
      },
    ],
  },
  leans_build: {
    heading: 'You should probably build your integration platform',
    description:
      "You have the technical capabilities and specialized needs, but be sure you're not underestimating maintenance costs. Building an integration platform is typically a multi-year commitment requiring 3-5 dedicated engineers.",
    buttons: [
      {
        label: 'Learn More',
        href: '/embedded-integration-platform',
        hierarchy: 'primary',
      },
    ],
  },
  clear_build: {
    heading: 'You should build your integration platform',
    description:
      "You have unique technical requirements, sufficient engineering resources, and integrations aren't mission-critical to closing deals. You should probably revisit this decision annually as your company scales.",
    buttons: [
      {
        label: 'Learn More',
        href: '/embedded-integration-platform',
        hierarchy: 'primary',
      },
    ],
  },
};

/**
 * Props for result content override from Builder
 */
export interface ResultContentProps {
  clearBuyHeading?: string;
  clearBuyDescription?: string;
  clearBuyButtons?: ButtonConfigProps[];
  leansBuyHeading?: string;
  leansBuyDescription?: string;
  leansBuyButtons?: ButtonConfigProps[];
  tossUpHeading?: string;
  tossUpDescription?: string;
  tossUpButtons?: ButtonConfigProps[];
  leansBuildHeading?: string;
  leansBuildDescription?: string;
  leansBuildButtons?: ButtonConfigProps[];
  clearBuildHeading?: string;
  clearBuildDescription?: string;
  clearBuildButtons?: ButtonConfigProps[];
}

/**
 * Get result content with Builder.io override support
 *
 * If Builder fields are provided, use them. Otherwise, fallback to
 * hardcoded defaults. This allows full customization while maintaining
 * default functionality.
 *
 * @param bucket - Result bucket to get content for
 * @param props - Optional Builder.io field overrides
 * @returns ResultContent with heading, description, and buttons
 */
export const getResultContent = (bucket: ResultBucket, props?: ResultContentProps): ResultContent => {
  // If no props provided, use defaults
  if (!props) {
    return DEFAULT_RESULT_CONTENT[bucket];
  }

  // Map bucket to prop names
  const propMap: Record<
    ResultBucket,
    { heading: keyof ResultContentProps; description: keyof ResultContentProps; buttons: keyof ResultContentProps }
  > = {
    clear_buy: { heading: 'clearBuyHeading', description: 'clearBuyDescription', buttons: 'clearBuyButtons' },
    leans_buy: { heading: 'leansBuyHeading', description: 'leansBuyDescription', buttons: 'leansBuyButtons' },
    toss_up: { heading: 'tossUpHeading', description: 'tossUpDescription', buttons: 'tossUpButtons' },
    leans_build: { heading: 'leansBuildHeading', description: 'leansBuildDescription', buttons: 'leansBuildButtons' },
    clear_build: { heading: 'clearBuildHeading', description: 'clearBuildDescription', buttons: 'clearBuildButtons' },
  };

  const { heading: headingKey, description: descriptionKey, buttons: buttonsKey } = propMap[bucket];
  const defaultContent = DEFAULT_RESULT_CONTENT[bucket];

  return {
    heading: (props[headingKey] as string) || defaultContent.heading,
    description: (props[descriptionKey] as string) || defaultContent.description,
    buttons: (props[buttonsKey] as ButtonConfigProps[]) || defaultContent.buttons,
  };
};
